import React, { useEffect } from 'react';
import { Badge, LoadingSpinner } from 'components/common';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { FaCar, FaPlane, FaBuilding, FaCheck, FaTimes, FaPlus, FaPaperclip, FaUsers } from 'react-icons/fa';
import { FiArrowRight, FiArrowUpRight, FiClock, FiUser, FiTrendingUp } from 'react-icons/fi';
import { fetchDashboard } from 'redux/booking/dashboardSlice';
import { getGlobalNotifications, markAllReadNotifications } from 'redux/notificationSlice';
import MyCalendarWidget from './MyCalendarWidget';
import useAuth from 'hooks/useAuth';

echarts.use([GridComponent, TooltipComponent, LegendComponent, BarChart, CanvasRenderer]);

// ── helpers ───────────────────────────────────────────────────────────────────
const stripHtml = (s) =>
  s?.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim() || '';

const timeAgo = (d) => {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  if (isNaN(ms)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  return dy < 7 ? `${dy}d ago`
    : new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CRM_BADGE = { 1:'new_pcv', 3:'quotation', 4:'quotation', 5:'quotation', 6:'tendering', 7:'pending', 8:'pending', 9:'negotiation', 10:'negotiation', 11:'signed', 12:'signed', 13:'won', 14:'execution', 15:'completed', 17:'lost', 18:'cancelled' };

const ACTIVITY_META = {
  create:  { icon: FaPlus,      bg: 'tw-bg-blue-100',   text: 'tw-text-blue-600'   },
  approve: { icon: FaCheck,     bg: 'tw-bg-green-100',  text: 'tw-text-green-600'  },
  reject:  { icon: FaTimes,     bg: 'tw-bg-red-100',    text: 'tw-text-red-500'    },
  cancel:  { icon: FaTimes,     bg: 'tw-bg-orange-100', text: 'tw-text-orange-500' },
  update:  { icon: FaPaperclip, bg: 'tw-bg-slate-100',  text: 'tw-text-slate-500'  },
};

// ── Primitives ────────────────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm ${className}`}>{children}</div>
);
Card.propTypes = { children: PropTypes.node.isRequired, className: PropTypes.string };

const CardHeader = ({ title, right }) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 sm:tw-px-5 tw-pt-4 sm:tw-pt-5 tw-pb-3.5 sm:tw-pb-4">
    <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-m-0">{title}</h3>
    {right}
  </div>
);
CardHeader.propTypes = { title: PropTypes.string.isRequired, right: PropTypes.node };

const ViewAll = ({ to, label = 'View all' }) => (
  <a href={to} target="_blank" rel="noopener noreferrer" className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-py-3 tw-rounded-lg tw-border tw-border-slate-200 tw-text-sm tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-50 tw-transition-colors tw-no-underline">
    {label} <FiArrowUpRight size={14} />
  </a>
);
ViewAll.propTypes = { to: PropTypes.string.isRequired, label: PropTypes.string };

const TypeTag = ({ type }) => (
  <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl ${
    type === 'travel' ? 'tw-bg-blue-50 tw-text-blue-600' : 'tw-bg-emerald-50 tw-text-emerald-600'
  }`}>
    {type === 'travel' ? <FaPlane size={9} /> : <FaCar size={9} />}
    {type === 'travel' ? 'Travel' : 'Car'}
  </span>
);
TypeTag.propTypes = { type: PropTypes.string.isRequired };

const Empty = ({ label }) => (
  <p className="tw-text-sm tw-text-slate-400 tw-text-center tw-py-8 tw-px-6">{label}</p>
);
Empty.propTypes = { label: PropTypes.string.isRequired };

// ── Stat card (compact, colorful) ────────────────────────────────────────────
const StatCard = ({ icon: Icon, iconBg, iconColor, accent, cardBg, label, value, diff, sub, loading }) => {
  const isPositive = diff > 0;
  const isNegative = diff < 0;

  return (
    <div className={`tw-group tw-relative tw-flex tw-flex-col tw-p-3.5 tw-pl-4 tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm hover:tw-shadow-lg hover:tw--translate-y-0.5 tw-transition-all tw-duration-200 tw-overflow-hidden ${cardBg || 'tw-bg-white'}`}>
      {/* Colored accent bar — the "điểm nhấn" */}
      <span className={`tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-1 ${accent || 'tw-bg-slate-300'}`} />

      {/* Top: icon left, badge right */}
      <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
        <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-transition-transform tw-duration-200 group-hover:tw-scale-110 ${iconBg || 'tw-bg-white'}`}>
          <Icon size={16} className={iconColor} />
        </div>
        {!loading && (
          <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
            {diff !== undefined && diff !== null && (
              <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-leading-none tw-bg-white tw-shadow-sm tw-ring-1 tw-ring-slate-100 ${
                isPositive ? 'tw-text-emerald-600' : isNegative ? 'tw-text-red-500' : 'tw-text-slate-400'
              }`}>
                {isPositive && <FiArrowUpRight size={9} />}{diff >= 0 ? '+' : ''}{diff}
              </span>
            )}
            {sub && (
              <span className="tw-text-[10px] tw-font-bold tw-text-amber-700 tw-bg-amber-100 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-leading-none">{sub}</span>
            )}
          </div>
        )}
      </div>

      {/* Number */}
      {loading
        ? <div className="tw-w-12 tw-h-7 tw-rounded-md tw-bg-slate-200/70 tw-animate-pulse tw-mb-1.5" />
        : <span className="tw-text-[26px] tw-font-black tw-text-slate-800 tw-leading-none tw-mb-1.5 tw-tracking-tight">{value ?? 0}</span>
      }

      {/* Label */}
      <p className="tw-text-[11px] tw-font-bold tw-text-slate-500 tw-m-0 tw-leading-none tw-truncate tw-uppercase tw-tracking-wide">{label}</p>
    </div>
  );
};
StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired, iconColor: PropTypes.string.isRequired,
  iconBg: PropTypes.string, accent: PropTypes.string,
  cardBg: PropTypes.string,
  label: PropTypes.string.isRequired, value: PropTypes.number,
  diff: PropTypes.number, sub: PropTypes.string, loading: PropTypes.bool,
};

// ── Stats panel (Booking + CRM combined inside one card) ─────────────────────
const StatsPanel = ({ bookingItems, crmItems, loading }) => (
  <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">

    {/* Booking section */}
    <div className="tw-px-5 tw-pt-5 tw-pb-4">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <div className="tw-w-0.5 tw-h-4 tw-rounded-xl tw-bg-blue-400 tw-flex-shrink-0" />
        <p className="tw-text-[11px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest tw-m-0">
          Booking — This Month
        </p>
      </div>
      <div className="tw-grid tw-grid-cols-1 min-[420px]:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
        {bookingItems.map((item, i) => {
          const { cellBg, ...props } = item;
          return <StatCard key={i} {...props} cardBg={cellBg} loading={loading} />;
        })}
      </div>
    </div>

    {/* Divider */}
    <div className="tw-h-px tw-bg-slate-100 tw-mx-5" />

    {/* CRM section */}
    <div className="tw-px-5 tw-pt-4 tw-pb-5">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <div className="tw-w-0.5 tw-h-4 tw-rounded-xl tw-bg-violet-400 tw-flex-shrink-0" />
        <p className="tw-text-[11px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest tw-m-0">
          CRM — My Activity This Month
        </p>
      </div>
      <div className="tw-grid tw-grid-cols-1 min-[420px]:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
        {crmItems.map((item, i) => {
          const { cellBg, ...props } = item;
          return <StatCard key={i} {...props} cardBg={cellBg} loading={loading} />;
        })}
      </div>
    </div>

  </div>
);
StatsPanel.propTypes = { bookingItems: PropTypes.array.isRequired, crmItems: PropTypes.array.isRequired, loading: PropTypes.bool };

// ── Bar Charts ────────────────────────────────────────────────────────────────
const chartBase = {
  backgroundColor: 'transparent',
  grid: { top: 14, right: 14, bottom: 28, left: 32, containLabel: true },
  legend: { right: 0, top: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10, itemGap: 16, textStyle: { color: '#64748b', fontSize: 11 } },
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#fff', borderColor: '#e2e8f0', borderWidth: 1, textStyle: { color: '#334155', fontSize: 12 } },
  xAxis: { type: 'category', axisLine: { lineStyle: { color: '#e2e8f0' } }, axisTick: { show: false }, axisLabel: { color: '#94a3b8', fontSize: 11 } },
  yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#94a3b8', fontSize: 11 } },
};

const TrendChart = ({ data }) => {
  const option = {
    ...chartBase,
    xAxis: { ...chartBase.xAxis, data: data.map(d => d.label) },
    series: [
      { name: 'Travel', type: 'bar', barMaxWidth: 22, barGap: '15%', data: data.map(d => d.travel), itemStyle: { color: '#60a5fa', borderRadius: [3, 3, 0, 0] } },
      { name: 'Car',    type: 'bar', barMaxWidth: 22,                 data: data.map(d => d.car),    itemStyle: { color: '#34d399', borderRadius: [3, 3, 0, 0] } },
    ],
  };
  return <ReactEChartsCore echarts={echarts} option={option} notMerge style={{ height: '200px', width: '100%' }} />;
};
TrendChart.propTypes = { data: PropTypes.array.isRequired };

const CrmTrendChart = ({ data }) => {
  const option = {
    ...chartBase,
    xAxis: { ...chartBase.xAxis, data: data.map(d => d.label) },
    series: [
      { name: 'Projects', type: 'bar', barMaxWidth: 22, barGap: '15%', data: data.map(d => d.projects || 0), itemStyle: { color: '#8b5cf6', borderRadius: [3, 3, 0, 0] } },
      { name: 'Leads',    type: 'bar', barMaxWidth: 22,                 data: data.map(d => d.leads || 0),    itemStyle: { color: '#f43f5e', borderRadius: [3, 3, 0, 0] } },
    ],
  };
  return <ReactEChartsCore echarts={echarts} option={option} notMerge style={{ height: '200px', width: '100%' }} />;
};
CrmTrendChart.propTypes = { data: PropTypes.array.isRequired };

// ── Pending Approvals ─────────────────────────────────────────────────────────
const PendingPanel = ({ items, totalCount, loading }) => (
  <Card>
    <CardHeader
      title="Pending Approvals"
      right={totalCount > 0 && (
        <span className="tw-text-xs tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-xl tw-bg-amber-100 tw-text-amber-700">
          {totalCount}
        </span>
      )}
    />
    {loading
      ? <div className="tw-flex tw-justify-center tw-py-6"><LoadingSpinner size="sm" /></div>
      : items.length === 0
        ? <Empty label="No pending approvals" />
        : (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-pb-3">
            {items.slice(0, 3).map((item, i) => (
              <div key={i} className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-bg-stone-50 ${item.overdue ? 'tw-border-l-[3px] tw-border-l-amber-400' : ''}`}>
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-xs tw-font-bold tw-text-slate-800 tw-truncate">{item.title}</p>
                  <p className="tw-text-[11px] tw-text-slate-400 tw-truncate tw-mt-0.5">
                    {[item.requester, item.date_range].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="tw-flex tw-flex-col tw-items-end tw-gap-0.5 tw-flex-shrink-0">
                  <TypeTag type={item.type} />
                  <span className={`tw-text-[10px] tw-font-semibold ${item.overdue ? 'tw-text-amber-600' : 'tw-text-slate-400'}`}>
                    {item.overdue ? `${item.hours_elapsed}h overdue` : `${item.hours_elapsed}h ago`}
                  </span>
                </div>
              </div>
            ))}
            {items.length > 3 && (
              <p className="tw-text-[11px] tw-text-slate-400 tw-text-center tw-py-1">
                +{items.length - 3} more pending
              </p>
            )}
          </div>
        )
    }
    <div className="tw-px-4 tw-pb-4">
      <ViewAll to="/booking/car/list" label="View all pending" />
    </div>
  </Card>
);
PendingPanel.propTypes = { items: PropTypes.array.isRequired, totalCount: PropTypes.number, loading: PropTypes.bool };

// ── My Calendar ───────────────────────────────────────────────────────────────

// ── Recent Activity ───────────────────────────────────────────────────────────
const ActivitySection = ({ activity, loading }) => (
  <Card>
    <CardHeader title="Recent Activity" />
    {loading
      ? <div className="tw-flex tw-justify-center tw-py-8"><LoadingSpinner size="sm" /></div>
      : activity.length === 0
        ? <Empty label="No recent activity" />
        : (
          <div className="tw-flex tw-flex-col tw-gap-0.5 tw-px-4 tw-pb-5">
            {activity.map((item, i) => {
              const meta = ACTIVITY_META[item.action] || ACTIVITY_META.update;
              const Icon = meta.icon;
              return (
                <div key={i} className="tw-flex tw-items-center tw-gap-3.5 tw-px-2 tw-py-2.5 tw-rounded-lg hover:tw-bg-stone-50 tw-transition-colors">
                  <span className={`tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-xl tw-flex tw-items-center tw-justify-center ${meta.bg}`}>
                    <Icon size={12} className={meta.text} />
                  </span>
                  <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate">{item.label}</p>
                    {item.comment && <p className="tw-text-xs tw-text-slate-400 tw-truncate tw-mt-0.5">{item.comment}</p>}
                  </div>
                  <span className="tw-text-[10px] tw-text-slate-400 tw-flex-shrink-0">{timeAgo(item.created_at)}</span>
                </div>
              );
            })}
          </div>
        )
    }
  </Card>
);
ActivitySection.propTypes = { activity: PropTypes.array.isRequired, loading: PropTypes.bool };

// ── Notifications ─────────────────────────────────────────────────────────────
const NotificationsSection = ({ todayData, earlierData, unReadCount, onMarkAll }) => {
  const all = [...(todayData || []), ...(earlierData || [])].slice(0, 6);
  const isUrgent = (n) => {
    const t = stripHtml(n.title || n.message || '').toLowerCase();
    return ['urgent', 'overdue', 'gấp', 'quá hạn'].some(k => t.includes(k));
  };
  return (
    <Card>
      <CardHeader
        title="Notifications"
        right={
          <div className="tw-flex tw-items-center tw-gap-2">
            {unReadCount > 0 && <span className="tw-text-xs tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-blue-100 tw-text-blue-700">{unReadCount}</span>}
            {unReadCount > 0 && (
              <button onClick={onMarkAll} className="tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 tw-outline-none tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
                Mark read
              </button>
            )}
          </div>
        }
      />
      {all.length === 0
        ? <Empty label="No notifications" />
        : (
          <div className="tw-flex tw-flex-col tw-gap-1.5 tw-px-4 tw-pb-5 tw-max-h-[280px] tw-overflow-y-auto">
            {all.map((n) => {
              const read   = n.read_at !== null && n.read_at !== undefined;
              const text   = stripHtml(n.title || n.message || '');
              const urgent = isUrgent(n);
              return (
                <div key={n.id} className={`tw-px-3 tw-py-2.5 tw-rounded-lg tw-border ${
                  urgent ? 'tw-bg-amber-50 tw-border-amber-200' :
                  !read  ? 'tw-bg-blue-50 tw-border-blue-100' :
                           'tw-bg-stone-50 tw-border-transparent'
                }`}>
                  <p className={`tw-text-xs tw-font-semibold tw-line-clamp-2 tw-leading-snug ${
                    urgent ? 'tw-text-amber-800' : !read ? 'tw-text-blue-800' : 'tw-text-slate-600'
                  }`}>{text || '(no message)'}</p>
                  {(n.created_at || n.createdAt) && (
                    <p className="tw-text-[10px] tw-mt-1 tw-text-slate-400">{timeAgo(n.created_at || n.createdAt)}</p>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </Card>
  );
};
NotificationsSection.propTypes = { todayData: PropTypes.array, earlierData: PropTypes.array, unReadCount: PropTypes.number, onMarkAll: PropTypes.func };

// ── CRM Recent Projects ───────────────────────────────────────────────────────
const CrmSection = ({ crm, loading }) => (
  <Card>
    <CardHeader
      title="CRM — Recent Projects"
      right={<Link to="/crm/projects/list" className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-violet-500 hover:tw-text-violet-700 tw-no-underline tw-transition-colors">View all <FiArrowRight size={11} /></Link>}
    />
    {loading
      ? <div className="tw-flex tw-justify-center tw-py-6"><LoadingSpinner size="sm" /></div>
      : crm.length === 0
        ? <Empty label="No recent CRM activity" />
        : (
          <div className="tw-divide-y tw-divide-slate-100/80 tw-pb-2">
            {crm.map((p) => (
              <Link key={p.id} to={`/crm/projects/${p.id}`}
                className="tw-flex tw-items-center tw-gap-3.5 tw-px-6 tw-py-3 hover:tw-bg-stone-50 tw-transition-colors tw-no-underline tw-group/cr">
                <div className="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded tw-bg-violet-50 tw-flex tw-items-center tw-justify-center">
                  <FaBuilding size={10} className="tw-text-violet-400" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-items-baseline tw-gap-2">
                    <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-shrink-0">{p.number}</span>
                    <span className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate group-hover/cr:tw-text-violet-700 tw-transition-colors">{p.name}</span>
                  </div>
                  {p.customer && <p className="tw-text-xs tw-text-slate-400 tw-truncate">{p.customer}</p>}
                </div>
                <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-flex-shrink-0">
                  <Badge variant={CRM_BADGE[p.status_id] || 'default'} size="sm">{p.status_name}</Badge>
                  <span className="tw-text-[10px] tw-text-slate-400">{timeAgo(p.updated_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        )
    }
  </Card>
);
CrmSection.propTypes = { crm: PropTypes.array.isRequired, loading: PropTypes.bool };

// ── Main ──────────────────────────────────────────────────────────────────────
const MyWorkSpacePage = () => {
  const dispatch = useDispatch();
  const { userName } = useAuth();
  const { stats, monthly_trend, pending_items, recent_activity, crm, loading } = useSelector(s => s.booking_dashboard);
  const { todayData, earlierData, unReadCount } = useSelector(s => s.notification);

  useEffect(() => { dispatch(fetchDashboard()); }, [dispatch]);

  useEffect(() => {
    dispatch(getGlobalNotifications());
    const poll = setInterval(() => dispatch(getGlobalNotifications()), 60_000);
    return () => clearInterval(poll);
  }, [dispatch]);

  const now   = new Date();
  const month = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const clock = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const hour  = now.getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const bookingStats = [
    { label: 'Travel',           icon: FaPlane,     iconBg: 'tw-bg-blue-100',    iconColor: 'tw-text-blue-600',    accent: 'tw-bg-blue-500',    cellBg: 'tw-bg-blue-50/60',    value: stats.travel_this_month, diff: stats.travel_this_month - stats.travel_prev_month },
    { label: 'Car Bookings',     icon: FaCar,       iconBg: 'tw-bg-emerald-100', iconColor: 'tw-text-emerald-600', accent: 'tw-bg-emerald-500', cellBg: 'tw-bg-emerald-50/60', value: stats.car_this_month,    diff: stats.car_this_month - stats.car_prev_month },
    { label: 'Pending Approval', icon: FiClock,     iconBg: 'tw-bg-amber-100',   iconColor: 'tw-text-amber-600',   accent: 'tw-bg-amber-500',   cellBg: 'tw-bg-amber-50/60',   value: stats.pending_approvals, sub: stats.overdue_count > 0 ? `${stats.overdue_count} overdue` : undefined },
    { label: 'New Bookings',     icon: FaPlus,      iconBg: 'tw-bg-purple-100',  iconColor: 'tw-text-purple-600',  accent: 'tw-bg-purple-500',  cellBg: 'tw-bg-purple-50/60',  value: stats.new_bookings_this, diff: stats.new_bookings_this - stats.new_bookings_prev },
  ];

  const crmStats = [
    { label: 'Projects',  icon: FaBuilding,   iconBg: 'tw-bg-violet-100', iconColor: 'tw-text-violet-600', accent: 'tw-bg-violet-500', cellBg: 'tw-bg-violet-50/60', value: stats.crm_projects_this,  diff: stats.crm_projects_this - stats.crm_projects_prev  },
    { label: 'Customers', icon: FaUsers,      iconBg: 'tw-bg-orange-100', iconColor: 'tw-text-orange-600', accent: 'tw-bg-orange-500', cellBg: 'tw-bg-orange-50/60', value: stats.crm_customers_this, diff: stats.crm_customers_this - stats.crm_customers_prev },
    { label: 'Contacts',  icon: FiUser,       iconBg: 'tw-bg-teal-100',   iconColor: 'tw-text-teal-600',   accent: 'tw-bg-teal-500',   cellBg: 'tw-bg-teal-50/60',   value: stats.crm_contacts_this,  diff: stats.crm_contacts_this - stats.crm_contacts_prev   },
    { label: 'Leads',     icon: FiTrendingUp, iconBg: 'tw-bg-rose-100',   iconColor: 'tw-text-rose-600',   accent: 'tw-bg-rose-500',   cellBg: 'tw-bg-rose-50/60',   value: stats.crm_leads_this,     diff: stats.crm_leads_this - stats.crm_leads_prev          },
  ];

  return (
    <div className="tw-w-full tw-max-w-[1600px] tw-mx-auto tw-px-3 sm:tw-px-5 xl:tw-px-8 tw-py-3 sm:tw-py-5 tw-space-y-4 tw-bg-[#f4f7fb]">

      {/* ── Header ── */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-pb-1">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-600 tw-to-violet-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-md tw-shadow-blue-500/25">
            <span className="tw-text-white tw-font-black tw-text-sm tw-tracking-tight">P</span>
          </div>
          <div>
            <p className="tw-text-xs tw-text-slate-400 tw-font-medium tw-mb-0.5">
              {greet}, <span className="tw-font-semibold tw-text-slate-700">{userName}</span>
            </p>
            <h1 className="tw-text-xl tw-font-black tw-text-slate-900 tw-m-0 tw-leading-tight">Workspace</h1>
            <p className="tw-text-xs tw-text-slate-400 tw-mt-0.5">{month} · {clock}</p>
          </div>
        </div>
        <div className="tw-flex tw-w-full sm:tw-w-auto tw-gap-2 tw-flex-wrap tw-flex-shrink-0">
          <Link to="/booking/travel/new-travel-request" className="tw-no-underline hover:tw-no-underline">
            <button className="tw-flex tw-flex-1 sm:tw-flex-none tw-items-center tw-justify-center tw-gap-1.5 tw-px-3.5 tw-py-2.5 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer tw-outline-none tw-no-underline">
              <FaPlane size={11} className="tw-text-blue-500" /> New Travel
            </button>
          </Link>
          <Link to="/booking/car/new-car-booking" className="tw-no-underline hover:tw-no-underline">
            <button className="tw-flex tw-flex-1 sm:tw-flex-none tw-items-center tw-justify-center tw-gap-1.5 tw-px-3.5 tw-py-2.5 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer tw-outline-none tw-no-underline">
              <FaCar size={11} className="tw-text-emerald-600" /> New Car
            </button>
          </Link>
        </div>
      </div>

      {/* ── Stats ── */}
      <StatsPanel bookingItems={bookingStats} crmItems={crmStats} loading={loading} />

      {/* ── Upcoming + [Pending, Notifications] ── */}
      <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-[1fr_340px] tw-gap-4">
        <MyCalendarWidget />
        <div className="tw-flex tw-flex-col tw-gap-4">
          <PendingPanel items={pending_items} totalCount={stats.pending_approvals} loading={loading} />
          <NotificationsSection
            todayData={todayData} earlierData={earlierData} unReadCount={unReadCount}
            onMarkAll={() => dispatch(markAllReadNotifications())}
          />
        </div>
      </div>

      {/* ── Charts ── */}
      <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-4">
        <Card>
          <CardHeader title="Bookings — Last 6 Months" />
          <div className="tw-px-4 tw-pb-4">
            {monthly_trend.length > 0
              ? <TrendChart data={monthly_trend} />
              : <div className="tw-flex tw-justify-center tw-py-8"><LoadingSpinner size="sm" /></div>
            }
          </div>
        </Card>
        <Card>
          <CardHeader
            title="CRM Activity — Last 6 Months"
            right={<span className="tw-text-xs tw-text-slate-400">Projects &amp; Leads</span>}
          />
          <div className="tw-px-4 tw-pb-4">
            {monthly_trend.length > 0
              ? <CrmTrendChart data={monthly_trend} />
              : <div className="tw-flex tw-justify-center tw-py-8"><LoadingSpinner size="sm" /></div>
            }
          </div>
        </Card>
      </div>

      {/* ── Activity + CRM Recent ── */}
      <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-4">
        <ActivitySection activity={recent_activity} loading={loading} />
        <CrmSection crm={crm} loading={loading} />
      </div>

    </div>
  );
};

export default MyWorkSpacePage;
